<template>
  <v-flex class="pl-2 pr-2 pb-2">
    <v-layout align-start justify-start row class="mb-4">
      <v-icon color="primary" style="font-size: 50px">design_services</v-icon>
      <h2 :style="`color: ${$vuetify.theme.primary}`" class="mt-2">
        {{ $translate("MAP_ICONS") }}
      </h2>
      <v-spacer></v-spacer>
      <v-btn fab right color="primary" @click="addNewIcon">
        <v-icon>add</v-icon>
      </v-btn>
    </v-layout>

    <v-spacer></v-spacer>

    <v-data-table
      :headers="headers"
      :items="mapIcons"
      class="elevation-1"
      :rows-per-page-items="[10]"
    >
      <template v-slot:items="props">
        <td>{{ props.item.Name }}</td>
        <td >
          <v-avatar size="35px">
            <v-img :src="props.item.GreenIcon" contain/>
          </v-avatar>
        </td>
        <td >
          <v-avatar size="35px">
            <v-img :src="props.item.YellowIcon" contain/>
          </v-avatar>
        </td>
        <td >
          <v-avatar size="35px">
            <v-img :src="props.item.RedIcon" contain/>
          </v-avatar>
        </td>
        <td class="justify-center">
          <v-btn
            small
            class="v-btn-tiny mr-2"
            color="primary"
            @click="editIcon(props.item)"
          >
            <v-icon small>edit</v-icon>
          </v-btn>

          <v-btn
            small
            class="v-btn-tiny mr-2"
            color="primary"
            @click="enableDeleteDialog(props.item)"
          >
            <v-icon small>delete</v-icon>
          </v-btn>
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="iconFormActive" max-width="1000px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ iconFormTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="iconForm" v-model="iconFormValid" lazy-validation>
            <v-layout column wrap justify-space-around>
              <v-flex>
                <v-layout row justify-center>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="iconSelected.Name"
                    :rules="iconNameRule"
                    :label="$translate('ICON_NAME')"
                  ></v-text-field>
                  <v-spacer></v-spacer>
                </v-layout>
              </v-flex>

              <v-flex>
                <v-layout row wrap justify-space-around>
                  <v-flex xs3>
                    <h4>{{$translate('GREEN_ICON')}}</h4>
                    <MapIconLoader 
                      v-if="iconFormActive"
                      :imageProps="greenIconProps"
                      v-on:uploadImage="setGreenIcon"
                      class="mt-2 mb-4" 
                      :style="`border: 1px solid black; border-radius: 5px`"
                    />
                  </v-flex>

                  <v-flex xs3>
                    <h4>{{$translate('YELLOW_ICON')}}</h4>
                    <MapIconLoader
                      v-if="iconFormActive"
                      :imageProps="yellowIconProps"
                      v-on:uploadImage="setYellowIcon"
                      class="mt-2 mb-4" 
                      :style="`border: 1px solid black; border-radius: 5px`"
                    />
                  </v-flex>

                  <v-flex xs3>
                    <h4>{{$translate('RED_ICON')}}</h4>
                    <MapIconLoader
                      v-if="iconFormActive"
                      :imageProps="redIconProps"
                      v-on:uploadImage="setRedIcon"
                      class="mt-2 mb-4" 
                      :style="`border: 1px solid black; border-radius: 5px`"
                    />
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary darken-1" round @click="cancelIconForm">{{
            $translate("CANCEL")
          }}</v-btn>
          <v-btn color="primary darken-1" round @click="saveMapIcon">{{
            $translate("SAVE")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialogActive" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">{{
            $translate("DELETE_MAP_ICON")
          }}</span>
        </v-card-title>

        <v-card-text>
          <v-layout wrap>
            <v-flex xs12>
              <v-text-field
                disabled
                v-model="iconSelected.Name"
                :label="$translate('ICON_NAME')"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="secondary darken-1"
            round
            @click="deleteDialogActive = false"
            >{{ $translate("CANCEL") }}</v-btn
          >
          <v-btn
            color="primary darken-1"
            round
            @click="deleteMapIcon"
            >{{ $translate("DELETE") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import * as dssRequest from "@/api/dssRequest";
import MapIconLoader from "@/components/media/MapIconLoader";
import MapIconObject from "@/objects/MapIconObject";

export default {
  name: "AccountMapIcons",
  components: {
    MapIconLoader
  },
  data() {
    return {
      mapIcons: [],
      headers: [
        {
          text: this.$translate("ICON_NAME"),
          align: "left",
          sortable: true,
          value: "Name",
        },
        {
          text: this.$translate("GREEN_ICON"),
          align: "left",
          sortable: false,
          value: "GreenIcon",
        },
        {
          text: this.$translate("YELLOW_ICON"),
          align: "left",
          sortable: false,
          value: "YellowIcon",
        },
        {
          text: this.$translate("RED_ICON"),
          align: "left",
          sortable: false,
          value: "RedIcon",
        },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "",
        }
      ],
      editMode: false,
      iconFormActive: false,
      iconForm: {},
      iconFormValid: false,
      iconNameRule: [(v) => !!v || 'A name for the icon is required'],
      iconImageRule: [(v) => !!v || 'An icon is required'],
      iconSelected: MapIconObject.initMapIcon(),
      greenIconProps: {},
      yellowIconProps: {},
      redIconProps: {},
      deleteDialogActive: false
    };
  },
  computed: {
    iconFormTitle() {
      if (this.editMode) {
        return this.$translate("EDIT_MAP_ICON");
      } else {
        return this.$translate("ADD_MAP_ICON");
      }
    }
  },
  mounted() {
    this.getMapIcons();
  },
  methods: {
    getMapIcons() {
      let payload = {};

      dssRequest.doRequest(
        response => {
          console.log(
            "The call [getMapIcons] on Account [%s] in component [Account_MapIcons] succeeded",
            this.$store.getters.getCurrentAccountId,
            response
          );
          this.mapIcons = [];
          if (response.data.hasOwnProperty("MapIcons") && response.data.MapIcons !== undefined) {
            this.mapIcons = [...response.data.MapIcons];
          }
        },
        error => {
          console.log(
            "The call [getMapIcons] on Account [%s] in component [Account_MapIcons] failed",
            this.$store.getters.getCurrentAccountId,
            error
          );
        },
        "getMapIcons",
        this.$store.getters.getCurrentAccountId,
        payload
      );
    },
    addNewIcon() {
      this.editMode = false;
      this.iconSelected = MapIconObject.initMapIcon();

      this.greenIconProps = {
        id: "greenIconLoader",
        src: ''
      }
      this.yellowIconProps = {
        id: "yellowIconLoader",
        src: ''
      }
      this.redIconProps = {
        id: "redIconLoader",
        src: ''
      }

      this.$refs.iconForm.resetValidation();
      this.iconFormActive = true;
    },
    editIcon(mapIcon) {
      this.iconSelected = Object.assign({}, mapIcon);

      this.greenIconProps = {
        id: "greenIconLoader",
        src: mapIcon.GreenIcon
      }
      this.yellowIconProps = {
        id: "yellowIconLoader",
        src: mapIcon.YellowIcon
      }
      this.redIconProps = {
        id: "redIconLoader",
        src: mapIcon.RedIcon
      }

      this.editMode = true;
      this.$refs.iconForm.resetValidation();
      this.iconFormActive = true;
    },
    enableDeleteDialog(mapIcon) {
      this.iconSelected = Object.assign({}, mapIcon);
      this.deleteDialogActive = true;
    },
    deleteMapIcon () {
      let payload = {
        IconId: this.iconSelected.IconId
      };

      dssRequest.doRequest(
        response => {
          console.log(
            "The call [deleteMapIcon] on Account [%s] in component [Account_MapIcons] succeeded",
            this.$store.getters.getCurrentAccountId,
            response
          );
          this.deleteDialogActive = false;

          this.getMapIcons();
        },
        error => {
          console.log(
            "The call [deleteMapIcon] on Account [%s] in component [Account_MapIcons] failed",
            this.$store.getters.getCurrentAccountId,
            error
          );
        },
        "deleteMapIcon",
        this.$store.getters.getCurrentAccountId,
        payload
      );
    },
    cancelIconForm() {
      this.iconForm = {};
      this.greenIconProps.src = '';
      this.yellowIconProps.src = '';
      this.redIconProps.src = '';
      this.$refs.iconForm.resetValidation();
      this.iconFormActive = false;
    },
    saveMapIcon() {
      if(!this.$refs.iconForm.validate()) return;

      let payload = {
        IconId: this.iconSelected.IconId,
        Name: this.iconSelected.Name,
        GreenIcon: this.iconSelected.GreenIcon,
        YellowIcon: this.iconSelected.YellowIcon,
        RedIcon: this.iconSelected.RedIcon
      };

      dssRequest.doRequest(
        response => {
          console.log(
            "The call [saveMapIcon] on Account [%s] in component [Account_MapIcons] succeeded",
            this.$store.getters.getCurrentAccountId,
            response
          );
          this.iconSelected = MapIconObject.initMapIcon();
          this.editMode = false;
          this.iconFormActive = false;

          this.getMapIcons();
        },
        error => {
          console.log(
            "The call [saveMapIcon] on Account [%s] in component [Account_MapIcons] failed",
            this.$store.getters.getCurrentAccountId,
            error
          );
        },
        "saveMapIcon",
        this.$store.getters.getCurrentAccountId,
        payload
      );
    },
    setGreenIcon(mapIcon) {
      this.iconSelected.GreenIcon = mapIcon;
      this.greenIconProps.src = mapIcon;
    },
    setYellowIcon(mapIcon) {
      this.iconSelected.YellowIcon = mapIcon;
      this.yellowIconProps.src = mapIcon;
    },
    setRedIcon(mapIcon) {
      this.iconSelected.RedIcon = mapIcon;
      this.redIconProps.src = mapIcon;
    }
  }
}
</script>

<style scoped>

</style>